import { Stepper } from '@design-system';
import { cn } from '@utils/cn';
import { useTranslation } from 'next-i18next';
import { ReactNode } from 'react';

import { FramingDrawer } from '../../../components/Checkout/Framing';
import {
  OrderReviewPreview,
  OrderReviewSummary,
} from '../../../components/Checkout/OrderReview';
import PageSection from '../../../components/Layout/PageSection';
import { useCheckoutCtx } from '../../../providers/CheckoutProvider';
import { CheckoutStep } from '../types';
import { STEPPER_STEPS } from '../utils/steps';
import { AddDetails } from './steps/AddDetails';
import { PlaceBid } from './steps/PlaceBid';
import { Review } from './steps/Review';
import { SelectWorks } from './steps/SelectWorks';
import { Success } from './steps/Success';

const STEP_COMPONENTS: Record<CheckoutStep, () => JSX.Element> = {
  bid: PlaceBid,
  details: AddDetails,
  review: Review,
  success: Success,
  works: SelectWorks,
};

export const Checkout = () => {
  const {
    allowMultipleSelections,
    framingDialog,
    framingSelections,
    orderedItems,
    step,
    summaryProps,
  } = useCheckoutCtx();
  const { t } = useTranslation('checkout');

  const activeIndex = step.steps.indexOf(step.step);
  const isSuccess = step.step === 'success';

  const stepperSteps = STEPPER_STEPS.filter((stepperStep) => {
    return step.steps.includes(stepperStep.step);
  }).map((stepperStep) => {
    const isCompleted = step.steps.indexOf(stepperStep.step) < activeIndex;

    return {
      isCompleted,
      label: t(stepperStep.labelKey),
      ...(isCompleted && !isSuccess
        ? { onClick: () => step.goToStep(stepperStep.step) }
        : {}),
    };
  });

  const sideByStep: Record<CheckoutStep, ReactNode> = {
    bid: <OrderReviewSummary {...summaryProps} showPreview />,
    details: (
      <OrderReviewSummary
        {...summaryProps}
        onAddFrame={framingDialog.openFor}
        showPreview
      />
    ),
    review: (
      <OrderReviewPreview
        images={orderedItems.map((item) => ({
          alt: item.title,
          id: item.id,
          src: item.image,
        }))}
      />
    ),
    success: null,
    works: <OrderReviewSummary {...summaryProps} showPreview />,
  };

  const StepComponent = STEP_COMPONENTS[step.step];
  const SideComponent = sideByStep[step.step];

  return (
    <>
      <PageSection className="py-12 lg:pt-0">
        <div className="relative grid grid-cols-12 gap-5">
          <div
            className={cn(
              'col-span-12 flex w-full flex-col gap-9 lg:gap-12 lg:p-12',
              SideComponent ? 'lg:col-span-6' : 'mx-auto max-w-xl',
            )}
          >
            {allowMultipleSelections ? undefined : (
              <Stepper steps={stepperSteps} />
            )}
            <StepComponent />
          </div>
          {SideComponent ? (
            <div className="col-span-12 lg:col-span-6">
              <aside className="sticky top-[4.5rem] hidden overflow-y-auto lg:ml-auto lg:block lg:max-w-lg lg:p-12">
                {SideComponent}
              </aside>
            </div>
          ) : null}
        </div>
      </PageSection>
      <FramingDrawer
        currencyCode={summaryProps.currencyCode}
        currentSelection={
          framingDialog.targetProduct
            ? framingSelections[framingDialog.targetProduct.id]
            : undefined
        }
        onClose={framingDialog.close}
        onConfirm={framingDialog.confirm}
        product={framingDialog.targetProduct}
        shown={framingDialog.targetProduct !== null}
        showApplyToAll={framingDialog.showApplyToAll}
      />
    </>
  );
};
